/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: FAQ section for Face Swap SEO page
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const faqItems: FAQItem[] = [
  {
    id: 'faq-1',
    question: 'What is video face swapping?',
    answer:
      "Video face swapping replaces a person's face in a video with another face using advanced AI technology. FalcoCut analyzes facial features frame by frame to preserve expressions, head movement, and lighting, ensuring smooth and natural-looking video results.",
  },
  {
    id: 'faq-2',
    question: 'Can I try face swapping for free?',
    answer:
      'Yes. FalcoCut offers 20 free credits so you can try video face swapping before upgrading. Free usage has limits on video length and processing priority. Paid plans unlock longer videos, higher resolution, and faster rendering.',
  },
  {
    id: 'faq-3',
    question: 'What are the video limits for Face Swap?',
    answer: (
      <>
        <p className="mb-3">Face Swap video limits depend on the selected version:</p>
        <p className="font-semibold text-gray-800 mb-2">V2.0 &ndash; Enhanced Fusion</p>
        <ul className="list-disc pl-5 space-y-1 mb-4">
          <li>Formats: MP4, MOV</li>
          <li>Max duration: 1 minute</li>
          <li>Max file size: 1GB</li>
          <li>Output: 4K supported</li>
        </ul>
        <p className="font-semibold text-gray-800 mb-2">V1.0 &ndash; Batch Supported</p>
        <ul className="list-disc pl-5 space-y-1 mb-4">
          <li>Formats: MP4, MOV</li>
          <li>Max duration: 10 minutes</li>
          <li>Max file size: 2GB</li>
          <li>Output: 4K supported</li>
        </ul>
        <p>
          Choose V2.0 for short, high-quality face fusion, or V1.0 for longer videos and
          batch processing.
        </p>
      </>
    ),
  },
  {
    id: 'faq-4',
    question: 'What video and reference image formats are supported?',
    answer: (
      <>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <li>Video formats: MP4, MOV</li>
          <li>Reference images (target face): JPG, PNG, WEBP</li>
        </ul>
        <p>
          You upload a video for face swapping and optionally provide a reference image to
          define the target face.
        </p>
      </>
    ),
  },
  {
    id: 'faq-5',
    question: 'How long does it take to process?',
    answer:
      'Processing time depends on video length and complexity. A short video (around 10 seconds) usually takes a few minutes. Longer or multi-face videos may take more time, especially during peak usage.',
  },
  {
    id: 'faq-6',
    question: 'Is my data safe?',
    answer:
      'Yes. All uploads are encrypted and processed securely. Files are automatically deleted after processing, and FalcoCut does not store or share your videos or images with third parties.',
  },
  {
    id: 'faq-7',
    question: 'Can I use swapped videos for commercial projects?',
    answer:
      'Yes, as long as you own the original video or have permission to use it. FalcoCut videos can be used for marketing, social media, ads, or client work. Illegal, deceptive, or harmful uses (such as misleading deepfakes) are strictly prohibited.',
  },
  {
    id: 'faq-8',
    question: 'Does FalcoCut support multiple faces?',
    answer:
      'Yes. FalcoCut supports multi-face video swapping, allowing you to replace several faces within the same video. Each face is tracked and swapped consistently throughout the footage.',
  },
  {
    id: 'faq-9',
    question: 'Does it support gender swap and celebrity swaps?',
    answer:
      'Yes. You can explore creative effects such as gender transformation or celebrity-style face swaps in videos, using reference images. These features are intended for creative and entertainment purposes.',
  },
  {
    id: 'faq-10',
    question: 'Is face swapping legal?',
    answer:
      "Video face swapping is legal for personal and creative use when done responsibly. Always obtain consent when using someone's likeness, especially for public or commercial projects, and avoid misleading or harmful applications.",
  },
  {
    id: 'faq-11',
    question: 'How do I get the best results?',
    answer: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Use clear, high-resolution videos with visible faces</li>
        <li>Ensure good lighting and minimal obstructions (e.g. sunglasses, hands)</li>
        <li>Use a clear reference image with a front-facing face</li>
        <li>
          Similar angles and expressions between the video and reference image produce
          more natural results
        </li>
      </ul>
    ),
  },
];

const FAQSection: React.FC = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
